/**
 * ExistingInstancesList - Shows owned instances for a card edition
 *
 * Displays all instances of a specific card (by scryfall_id) that the user owns,
 * with +1 buttons to quickly increment quantity on existing instances.
 */
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { IoAdd } from 'react-icons/io5';
import { toast } from 'sonner';

import { useCollectionQuery, useUpdateQuantityMutation } from '@/features/collection/hooks/api';
import { CONDITION_LABELS, FINISH_LABELS, LANGUAGE_LABELS } from '@/lib/cardUtils';
import type { EnrichedCollectionCard } from '@/types/card';

// ============================================================================
// Types
// ============================================================================

export interface ExistingInstancesListProps {
  /** Scryfall ID of the card to show instances for */
  scryfallId: string;
  /** Called after successfully incrementing an instance */
  onIncremented?: (instance: EnrichedCollectionCard) => void;
}

// ============================================================================
// Subcomponents
// ============================================================================

interface InstanceRowProps {
  instance: EnrichedCollectionCard;
  onIncrement: () => void;
  isPending: boolean;
}

function InstanceRow({ instance, onIncrement, isPending }: InstanceRowProps): ReactElement {
  const conditionLabel = CONDITION_LABELS[instance.condition] ?? instance.condition;
  const finishLabel = FINISH_LABELS[instance.finish] ?? instance.finish;
  const languageLabel = LANGUAGE_LABELS[instance.language] ?? instance.language;
  const folderPath = instance.folder_path ?? 'Unfiled';

  return (
    <div className="border-storm flex items-center justify-between border-b px-3 py-2 last:border-b-0">
      <div className="flex flex-col gap-0.5">
        <div className="text-silver flex items-center gap-2 text-sm">
          <span className="text-pearl font-medium">{conditionLabel}</span>
          <span className="text-ash">·</span>
          <span>{finishLabel}</span>
          <span className="text-ash">·</span>
          <span>{languageLabel}</span>
        </div>
        <div className="text-ash flex items-center gap-2 text-xs">
          <span>/{folderPath}</span>
          <span className="text-ash">·</span>
          <span className="text-silver">×{instance.quantity}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onIncrement}
        disabled={isPending}
        className="bg-aether/20 hover:bg-aether/30 text-aether flex h-8 w-8 items-center justify-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={`Add one more copy (current: ${instance.quantity})`}
      >
        <IoAdd className="h-4 w-4" />
      </button>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function ExistingInstancesList({ scryfallId, onIncremented }: ExistingInstancesListProps): ReactElement | null {
  const { data: collection } = useCollectionQuery();
  const updateQuantityMutation = useUpdateQuantityMutation();

  // Find all instances of this card
  const instances = useMemo(() => {
    if (!collection) return [];
    return collection.filter((c) => c.scryfall_id === scryfallId);
  }, [collection, scryfallId]);

  const handleIncrement = (instance: EnrichedCollectionCard) => {
    updateQuantityMutation.mutate(
      { cardId: instance.id, quantity: instance.quantity + 1 },
      {
        onSuccess: () => {
          toast.success(`Updated to ${instance.quantity + 1}× ${instance.card.name}`);
          onIncremented?.(instance);
        },
      },
    );
  };

  // Don't render if no existing instances
  if (instances.length === 0) {
    return null;
  }

  return (
    <div className="bg-slate/50 border-storm rounded-xl border">
      <div className="border-storm border-b px-3 py-2">
        <h4 className="text-ash text-xs font-medium tracking-widest uppercase">You Own This Edition</h4>
      </div>
      <div className="max-h-32 overflow-y-auto">
        {instances.map((instance) => (
          <InstanceRow
            key={instance.id}
            instance={instance}
            onIncrement={() => handleIncrement(instance)}
            isPending={updateQuantityMutation.isPending}
          />
        ))}
      </div>
    </div>
  );
}
